/**
 * Geocode an address using the free Nominatim (OpenStreetMap) API.
 * Returns { latitude, longitude } or null if not found.
 */
export async function geocodeAddress(
  address?: string,
  city?: string,
  postal?: string
): Promise<{ latitude: number; longitude: number } | null> {
  const parts = [address, postal, city].filter(Boolean).join(", ");
  if (!parts.trim()) return null;

  try {
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(parts)}&format=json&limit=1`;
    const res = await fetch(url, {
      headers: { "Accept-Language": "fr", "User-Agent": "HexaflowApp/1.0" },
    });
    const data = await res.json();
    if (!data || data.length === 0) return null;
    return {
      latitude: parseFloat(data[0].lat),
      longitude: parseFloat(data[0].lon),
    };
  } catch {
    return null;
  }
}

// Cache reverse-geocode lookups so a gallery doesn't hammer Nominatim (which
// rate-limits to ~1 req/s) for photos taken at the same spot.
const reverseGeocodeCache = new Map<string, string | null>();

/**
 * Reverse-geocode coordinates to a short human-readable place (e.g.
 * "12 Rue de la Paix, Le Poiré-sur-Vie"). Returns null on failure.
 */
export async function reverseGeocode(
  latitude?: number,
  longitude?: number,
): Promise<string | null> {
  if (latitude == null || longitude == null) return null;
  const key = `${latitude.toFixed(5)},${longitude.toFixed(5)}`;
  if (reverseGeocodeCache.has(key)) return reverseGeocodeCache.get(key) ?? null;

  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&zoom=18`;
    const res = await fetch(url, {
      headers: { "Accept-Language": "fr", "User-Agent": "HexaflowApp/1.0" },
    });
    const data = await res.json();
    const a = data?.address || {};
    const road = [a.house_number, a.road].filter(Boolean).join(" ");
    const town = a.village || a.town || a.city || a.municipality || a.county;
    const label = [road, town].filter(Boolean).join(", ") || data?.display_name || null;
    reverseGeocodeCache.set(key, label);
    return label;
  } catch {
    reverseGeocodeCache.set(key, null);
    return null;
  }
}
